import { resolve } from 'acast-www-utils';
import { runOperation } from 'app/operations';
import sync from 'lib/resource-sync';
import createReducer from './create-reducer';
import injectResource from '../inject';
import setupReducer from '../helpers/setup-reducer';
// TODO: decouple from Create
// runOperation() comes from Create at the moment.

function getDescriptorFromConnectArgs(args) {
  if (args.length === 1 && typeof args[0] === 'function') {
    return { props: args[0] };
  }

  if (args.length === 2 && typeof args[0] === 'string') {
    return {
      props: props => ({
        [args[0]]: typeof args[1] === 'string' ? resolve(props, args[1], args[1]) : args[1],
      }),
    };
  }

  return undefined; // undefined
}

function invoke(scope, fnName, ...args) {
  if (typeof scope[fnName] === 'function') {
    // eslint-disable-next-line prefer-spread
    scope[fnName].apply(scope, args);
  }
}

export default (Actions, Schema, customProperties) => {
  setupReducer(Actions, Schema, createReducer);

  const resourceKeyName = Schema.name.toLowerCase();

  return {
    Schema,

    connect(...args) {
      const descriptor = getDescriptorFromConnectArgs(args);

      if (!descriptor) {
        throw new Error(`@connect ${Schema.name}: invalid arguments`);
      }

      return TargetComponent =>
        injectResource(TargetComponent, Schema, this, descriptor, resourceKeyName);
    },

    provide(target, ...descriptor) {
      const withResource = this.connect(...descriptor);
      return withResource(target);
    },

    create(entity, ...rest) {
      return dispatch => {
        if (!entity && typeof entity.getState !== 'function') {
          return Promise.reject(`Bad arguments to ${Schema.name}.create()`);
        }

        const body = entity.getState();
        const tempId = `${body.id}/create`;
        let didInsert = false;

        return new Promise((succeed, reject) => {
          sync.enqueue(sync.CREATE, body, {
            run(data) {
              return dispatch(runOperation(`${resourceKeyName}/create`, tempId, data, ...rest));
            },

            accept(tx) {
              if (didInsert) {
                dispatch(Actions.update(tx.entity.id, tx.entity));
              } else {
                dispatch(Actions.add(tx.entity.id, tx.entity));
                didInsert = true;
              }
              if (tx.isComplete) {
                succeed(tx.entity);
              }
            },

            fail(tx, error) {
              // eslint-disable-next-line
              console.log('sync.CREATE//fail', error);
              reject(error);
            },
          });
        });
      };
    },

    read(id, forceFetch = false, ...rest) {
      return (dispatch, getState) => {
        // if the entity exists, do not trigger a query, just
        // resolve immediately.
        if (forceFetch !== true) {
          const root = getState();
          const state = root[resourceKeyName];

          if (state.get(id)) {
            return Promise.resolve(state.get(id));
          }
        }

        /**
         * NOTE: We have to return a promise here since the .inject
         *       helper relies on it to catch potential errors.
         */
        return new Promise((succeed, reject) => {
          sync.enqueue(
            sync.READ,
            { id },
            {
              schemaName: Schema.name,

              run() {
                return dispatch(runOperation(`${resourceKeyName}/read`, id, id, ...rest));
              },

              accept(tx) {
                if (tx.isComplete) {
                  dispatch(Actions.add(id, tx.entity));
                  succeed(tx.entity);
                }
              },

              fail(tx, error) {
                // eslint-disable-next-line
                console.log('sync.READ//fail', error);
                reject(error);
              },
            }
          );
        });
      };
    },

    update(id, entity, options = {}) {
      const self = this;
      let modified = entity;

      if (resolve(options, 'noModifiedOverride', false) !== true) {
        modified = entity.merge({
          modified: new Date().toISOString(),
        });
      }

      return dispatch =>
        new Promise((succeed, reject) => {
          invoke(self, 'onBeforeUpdate', modified);
          sync.enqueue(sync.UPDATE, modified.getState(), {
            run(data) {
              return dispatch(runOperation(`${resourceKeyName}/update`, id, data));
            },

            accept(tx) {
              dispatch(Actions.update(id, tx.entity));
              if (tx.isComplete) {
                succeed(tx.entity);
                invoke(self, 'onAfterUpdate', tx.entity);
              }
            },

            fail(tx, error) {
              /**
               * TODO: Currently, if the operation fails it will add the error
               *       to the client state. Here we can respond to the type of
               *       error in case we want custom logic (retries etc.).
               */

              // Leave the below for now until we have some type of client
              // error handling that will actually notify users of errors.
              // eslint-disable-next-line
              console.log('sync.UPDATE//fail', error);
              reject(error);
            },
          });
        });
    },

    delete(id) {
      return dispatch =>
        dispatch(runOperation(`${resourceKeyName}/delete`, id, id)).then(result =>
          dispatch(Actions.delete(id, result.value))
        );
    },

    ...customProperties,
  };
};
